import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, PIPELINE_STAGES, stageColor } from "../lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const Pipeline = () => {
  const [projects, setProjects] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ client_name: "", project_title: "", project_type: "", shoot_date: "", deposit_amount: 0, final_amount: 0 });

  const load = async () => {
    const { data } = await api.get("/projects");
    setProjects(data);
  };
  useEffect(() => { load(); }, []);

  const onDragStart = (pid) => (e) => {
    setDragging(pid);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (stage) => (e) => {
    e.preventDefault();
    setDragOver(stage);
  };
  const onDrop = (stage) => async (e) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragging) return;
    const p = projects.find((x) => x.id === dragging);
    setDragging(null);
    if (!p || p.status === stage) return;
    setProjects((ps) => ps.map((x) => (x.id === p.id ? { ...x, status: stage } : x)));
    try {
      await api.patch(`/projects/${p.id}/status`, { status: stage });
      toast.success(`Moved to ${PIPELINE_STAGES.find((s) => s.key === stage).label}`);
    } catch {
      toast.error("Failed to update");
      load();
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    try {
      await api.post("/projects", form);
      toast.success("Project created");
      setShowNew(false);
      setForm({ client_name: "", project_title: "", project_type: "", shoot_date: "", deposit_amount: 0, final_amount: 0 });
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-12 h-screen flex flex-col">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="label-caps mb-2">Production</div>
          <h1 className="font-editorial text-6xl tracking-tighter leading-none">Pipeline</h1>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary inline-flex items-center gap-2" data-testid="new-project-btn">
          <Plus size={14} /> New Project
        </button>
      </div>

      <div className="flex gap-4 overflow-x-auto flex-1 pb-4" data-testid="kanban-board">
        {PIPELINE_STAGES.map((stage) => {
          const cards = projects.filter((p) => p.status === stage.key);
          return (
            <div
              key={stage.key}
              className={`kanban-col flex flex-col ${dragOver === stage.key ? "drag-over" : ""}`}
              onDragOver={onDragOver(stage.key)}
              onDrop={onDrop(stage.key)}
              onDragLeave={() => setDragOver(null)}
              data-testid={`kanban-column-${stage.key}`}
            >
              <div className="flex items-center justify-between mb-4 px-2">
                <div className="flex items-center gap-2">
                  <span className="status-dot" style={{ background: stage.color }} />
                  <span className="label-caps">{stage.label}</span>
                </div>
                <span className="font-mono-alt text-xs text-white/40">{cards.length}</span>
              </div>
              <div className="space-y-3 overflow-y-auto flex-1">
                {cards.map((p) => (
                  <Link
                    to={`/admin/projects/${p.id}`}
                    key={p.id}
                    draggable
                    onDragStart={onDragStart(p.id)}
                    className={`kanban-card block ${dragging === p.id ? "dragging" : ""}`}
                    style={{ borderLeft: `2px solid ${stageColor(p.status)}` }}
                    data-testid={`card-${p.id}`}
                  >
                    <div className="label-caps mb-2">{p.project_type || "Project"}</div>
                    <div className="font-editorial text-xl leading-tight mb-2">{p.project_title}</div>
                    <div className="text-sm text-white/60 mb-3">{p.client_name}</div>
                    <div className="flex items-center justify-between">
                      <div className="font-mono-alt text-xs text-white/40">
                        {p.shoot_date || "no date"}
                      </div>
                      <div className="flex gap-1">
                        {p.deposit_paid && <span className="px-2 py-0.5 text-[10px] font-mono-alt bg-emerald-500/20 text-emerald-400">DEP</span>}
                        {p.final_paid && <span className="px-2 py-0.5 text-[10px] font-mono-alt bg-emerald-500/20 text-emerald-400">PAID</span>}
                      </div>
                    </div>
                  </Link>
                ))}
                {cards.length === 0 && (
                  <div className="border border-dashed border-white/5 p-8 text-center text-xs text-white/20">
                    Drop cards here
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showNew && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-8" onClick={() => setShowNew(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={createProject}
            className="bg-[#0A0A0A] border border-white/10 p-10 w-full max-w-xl">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-editorial text-3xl">New Project</h2>
              <button type="button" onClick={() => setShowNew(false)} data-testid="close-new-project"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="label-caps mb-2 block">Client Name</label>
                <input required className="input-editorial" value={form.client_name} onChange={(e) => setForm({...form, client_name: e.target.value})} data-testid="np-client"/>
              </div>
              <div>
                <label className="label-caps mb-2 block">Project Title</label>
                <input required className="input-editorial" value={form.project_title} onChange={(e) => setForm({...form, project_title: e.target.value})} data-testid="np-title"/>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-caps mb-2 block">Type</label>
                  <input className="input-editorial" value={form.project_type} onChange={(e) => setForm({...form, project_type: e.target.value})} data-testid="np-type"/>
                </div>
                <div>
                  <label className="label-caps mb-2 block">Shoot Date</label>
                  <input type="date" className="input-editorial" value={form.shoot_date} onChange={(e) => setForm({...form, shoot_date: e.target.value})} data-testid="np-date"/>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-caps mb-2 block">Deposit ($)</label>
                  <input type="number" step="0.01" className="input-editorial" value={form.deposit_amount} onChange={(e) => setForm({...form, deposit_amount: parseFloat(e.target.value) || 0})} data-testid="np-deposit"/>
                </div>
                <div>
                  <label className="label-caps mb-2 block">Final ($)</label>
                  <input type="number" step="0.01" className="input-editorial" value={form.final_amount} onChange={(e) => setForm({...form, final_amount: parseFloat(e.target.value) || 0})} data-testid="np-final"/>
                </div>
              </div>
            </div>
            <button type="submit" className="btn-primary w-full mt-8" data-testid="np-submit">Create Project</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default Pipeline;
